/**
 * Settings -> Notifications: the admin config screen (module x channel toggles) and Delivery
 * Activity (the read model answering "did this notification actually go out, and if not, why").
 *
 * HR/ADMIN ONLY, PER THE SETTLED PRODUCT DECISION: there is no employee-facing preference surface
 * anywhere in this contract — see `notifications.contract.ts` for the employee-facing bell API,
 * which is deliberately a separate, unrelated set of routes with no admin permission on any of them.
 * `notification.config:*` and `notification.delivery:read` (permissions.ts) gate everything here;
 * neither `manager` nor `employee` holds any of the three (roles.ts).
 *
 * `pathPrefix` style and DTO-schemas-above-the-router convention both taken from
 * `integrations.contract.ts` and `roles.contract.ts`.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { NOTIFICATION_CHANNELS } from '../enums.js';
import { NOTIFICATION_MODULES } from '../notifications/catalog.js';
import { routeMeta } from './meta.js';
import {
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  uuidSchema,
} from '../schemas/common.js';

const c = initContract();

export const notificationModuleSchema = z.enum(NOTIFICATION_MODULES);
export const notificationChannelSettingChannelSchema = z.enum(NOTIFICATION_CHANNELS);

// ---------------------------------------------------------------------------
// Channel settings — the module x channel grid
// ---------------------------------------------------------------------------

export const notificationChannelSettingSchema = z.object({
  orgId: orgIdSchema,
  module: notificationModuleSchema,
  channel: notificationChannelSettingChannelSchema,
  enabled: z.boolean(),
  /** Who last flipped this row; null for the lazy-seeded default nobody has touched yet. */
  updatedBy: z.string().nullable(),
  updatedAt: instantSchema,
});
export type NotificationChannelSetting = z.infer<typeof notificationChannelSettingSchema>;

export const updateNotificationChannelSettingSchema = z.object({
  module: notificationModuleSchema,
  channel: notificationChannelSettingChannelSchema,
  enabled: z.boolean(),
});
export type UpdateNotificationChannelSetting = z.infer<
  typeof updateNotificationChannelSettingSchema
>;

export const updateNotificationChannelsBodySchema = z.array(updateNotificationChannelSettingSchema);

// ---------------------------------------------------------------------------
// Delivery activity — the per-recipient, per-channel read model
// ---------------------------------------------------------------------------

/**
 * A delivery row's lifecycle status. Distinct from `packages/notify`'s `ChannelOutcome`
 * (`sent | suppressed | deferred | skipped | failed`), which is the result of one attempt — this is
 * the row's CURRENT state, and starts `queued` before any attempt has run. The schema migration
 * (plan PR 2) is the source of truth for the actual Prisma enum; this must stay in step with it.
 */
export const notificationDeliveryStatusSchema = z.enum([
  'queued',
  'sent',
  'suppressed',
  'deferred',
  'skipped',
  'failed',
]);

/**
 * Which recipient shape a delivery row is for. `external` covers the candidate correspondence sent
 * via `notifyExternal()` (design rule 9: it still writes delivery rows even though it bypasses the
 * module x channel toggles, so "did the offer letter go out?" stays answerable here) — there is no
 * `Person` row and no `notificationId` behind it, hence both being nullable below.
 */
export const notificationRecipientKindSchema = z.enum(['person', 'external']);

export const notificationDeliveryActivityItemSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  /** Null for an `external` delivery — a candidate has no in-app `Notification` row. */
  notificationId: uuidSchema.nullable(),
  /** Null for an `external` delivery — the recipient is a candidate, not a `Person`. */
  personId: uuidSchema.nullable(),
  recipientKind: notificationRecipientKindSchema,
  /** The person's name, so the log reads as people rather than ids. Null for an external delivery. */
  recipientName: z.string().nullable(),
  recipientEmail: z.string().nullable(),
  key: z.string(),
  module: notificationModuleSchema,
  channel: notificationChannelSettingChannelSchema,
  status: notificationDeliveryStatusSchema,
  /** Why: a support answer for "I never got the email" — see `ChannelResult.reason` in `@hrms/notify`. */
  reason: z.string().nullable(),
  /** The provider's id for the accepted message, when it returned one. */
  providerRef: z.string().nullable(),
  attemptCount: z.number().int().nonnegative(),
  queuedAt: instantSchema,
  sentAt: instantSchema.nullable(),
  failedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type NotificationDeliveryActivityItem = z.infer<
  typeof notificationDeliveryActivityItemSchema
>;

export const listDeliveryActivityQuerySchema = paginationQuerySchema.extend({
  /** Inclusive bounds on `queuedAt`, for the Delivery Activity table's date-range picker. */
  dateFrom: instantSchema.optional(),
  dateTo: instantSchema.optional(),
  module: notificationModuleSchema.optional(),
  channel: notificationChannelSettingChannelSchema.optional(),
  status: notificationDeliveryStatusSchema.optional(),
  personId: uuidSchema.optional(),
  /** Exact catalogue key, e.g. `attendance.late_arrival`. */
  key: z.string().max(120).optional(),
  /** Free-text search on the recipient's name or email (case-insensitive, substring). */
  q: z.string().trim().min(1).max(100).optional(),
});
export type ListDeliveryActivityQuery = z.infer<typeof listDeliveryActivityQuerySchema>;

/**
 * Delivery health for a period: how many notifications went out and how many did not, per channel.
 * `failedRatio` is failed / (sent + failed) and is null when nothing was attempted (so an idle
 * period does not read as healthy or unhealthy). Counts cover the caller's own org only.
 */
export const notificationDeliverySummarySchema = z.object({
  dateFrom: instantSchema,
  dateTo: instantSchema,
  total: z.number().int().nonnegative(),
  byStatus: z.record(notificationDeliveryStatusSchema, z.number().int().nonnegative()),
  byChannel: z.array(
    z.object({
      channel: notificationChannelSettingChannelSchema,
      total: z.number().int().nonnegative(),
      sent: z.number().int().nonnegative(),
      failed: z.number().int().nonnegative(),
      suppressed: z.number().int().nonnegative(),
    }),
  ),
  failedRatio: z.number().min(0).max(1).nullable(),
});
export type NotificationDeliverySummary = z.infer<typeof notificationDeliverySummarySchema>;

export const getDeliverySummaryQuerySchema = z.object({
  dateFrom: instantSchema,
  dateTo: instantSchema,
  module: notificationModuleSchema.optional(),
  channel: notificationChannelSettingChannelSchema.optional(),
});

/**
 * One row of the delivery-log export. Carries the recipient's name and email (the on-screen table
 * shows neither), so the export requires `notification.delivery:read` like the log itself and is
 * bounded: a date range is mandatory and the result is capped (`truncated` says when it was).
 */
export const notificationDeliveryExportItemSchema = notificationDeliveryActivityItemSchema;
export type NotificationDeliveryExportItem = z.infer<typeof notificationDeliveryExportItemSchema>;

export const NOTIFICATION_DELIVERY_EXPORT_MAX_ROWS = 10_000;

export const exportDeliveryActivityQuerySchema = listDeliveryActivityQuerySchema
  .omit({ cursor: true, limit: true })
  .extend({ dateFrom: instantSchema, dateTo: instantSchema });
export type ExportDeliveryActivityQuery = z.infer<typeof exportDeliveryActivityQuerySchema>;

// ---------------------------------------------------------------------------
// Per-notification overrides — one catalogue key on one channel (T1.2)
// ---------------------------------------------------------------------------

/**
 * An override of the module x channel grid for ONE notification (`attendance.late_arrival`) on ONE
 * channel. Precedence in `decide()`: mandatory notice > this override > the module row > the channel
 * default. Only explicit overrides are returned; a notification with no row inherits from its module.
 */
export const notificationEventSettingSchema = z.object({
  orgId: orgIdSchema,
  eventKey: z.string(),
  channel: notificationChannelSettingChannelSchema,
  enabled: z.boolean(),
  updatedBy: z.string().nullable(),
  updatedAt: instantSchema,
});
export type NotificationEventSetting = z.infer<typeof notificationEventSettingSchema>;

export const updateNotificationEventSettingSchema = z.object({
  eventKey: z.string().min(1).max(120),
  channel: notificationChannelSettingChannelSchema,
  /** `null` removes the override, so the notification inherits from its module again. */
  enabled: z.boolean().nullable(),
});
export type UpdateNotificationEventSetting = z.infer<typeof updateNotificationEventSettingSchema>;

export const updateNotificationEventSettingsBodySchema = z
  .array(updateNotificationEventSettingSchema)
  .min(1)
  .max(200);

// ---------------------------------------------------------------------------
// Audience for HR-directed notifications (T1.3)
// ---------------------------------------------------------------------------

/** The notifications whose HR recipients an admin may choose. Every other key ignores this setting. */
export const AUDIENCE_CONFIGURABLE_EVENT_KEYS = [
  'attendance.late_arrival',
  'attendance.start_summary',
  'attendance.checked_in_with_plan',
  // Travel Desk (ADR 0030): the desk's own queue alerts and the overdue-advance chase. The
  // emitters send the team lead's copy / the employee's copy separately.
  'travel.desk.unassigned',
  'travel.desk.breached',
  'expense.advance.overdue',
] as const;
export type AudienceConfigurableEventKey = (typeof AUDIENCE_CONFIGURABLE_EVENT_KEYS)[number];

/**
 * `hr_all`      every HR grant (hr_admin / hr_manager) whose scope covers the employee — the behaviour
 *               before this setting existed, and the behaviour when no row is stored;
 * `hr_selected` only `personIds`, and still only for employees a grant of theirs covers;
 * `off`         nobody in HR. (The employee's own copy and the manager copy are separate keys.)
 */
export const NOTIFICATION_AUDIENCE_MODES = ['hr_all', 'hr_selected', 'off'] as const;
export const notificationAudienceModeSchema = z.enum(NOTIFICATION_AUDIENCE_MODES);
export type NotificationAudienceMode = z.infer<typeof notificationAudienceModeSchema>;

/**
 * Who receives a configurable notification. Any combination; with every group empty/off the
 * notification is simply not sent. Each recipient only ever sees employees they are allowed to see:
 *  - `mode` / `personIds`: the HR part (above);
 *  - `includeManagers`: each employee's reporting manager, for their own team only;
 *  - `extraPersonIds`: named people, who receive it for every employee (chosen explicitly by an admin);
 *  - `roleKeys`: holders of these roles, for the employees their grant covers.
 */
export const notificationAudienceSettingSchema = z.object({
  orgId: orgIdSchema,
  eventKey: z.enum(AUDIENCE_CONFIGURABLE_EVENT_KEYS),
  mode: notificationAudienceModeSchema,
  personIds: z.array(uuidSchema),
  includeManagers: z.boolean(),
  extraPersonIds: z.array(uuidSchema),
  roleKeys: z.array(z.string()),
  /** False when nothing is stored and these are the defaults. */
  customised: z.boolean(),
  updatedAt: instantSchema,
});
export type NotificationAudienceSetting = z.infer<typeof notificationAudienceSettingSchema>;

/** What an unconfigured company gets — today's behaviour for each notification. */
export const DEFAULT_NOTIFICATION_AUDIENCE: Readonly<
  Record<
    AudienceConfigurableEventKey,
    Pick<
      NotificationAudienceSetting,
      'mode' | 'personIds' | 'includeManagers' | 'extraPersonIds' | 'roleKeys'
    >
  >
> = {
  'attendance.late_arrival': {
    mode: 'hr_all',
    personIds: [],
    includeManagers: false,
    extraPersonIds: [],
    roleKeys: [],
  },
  'attendance.start_summary': {
    mode: 'hr_all',
    personIds: [],
    includeManagers: true,
    extraPersonIds: [],
    roleKeys: [],
  },
  // The employee's reporting manager by default (Arham Fintech's ask); the email itself is off until
  // the company switches it on.
  'attendance.checked_in_with_plan': {
    mode: 'off',
    personIds: [],
    includeManagers: true,
    extraPersonIds: [],
    roleKeys: [],
  },
  // Off = nobody beyond the role holders below: the Travel Desk Manager sees the desk's queue
  // alerts by default; the team lead's copy is sent by the emitter.
  'travel.desk.unassigned': {
    mode: 'off',
    personIds: [],
    includeManagers: false,
    extraPersonIds: [],
    roleKeys: ['travel_desk_manager'],
  },
  'travel.desk.breached': {
    mode: 'off',
    personIds: [],
    includeManagers: false,
    extraPersonIds: [],
    roleKeys: ['travel_desk_manager'],
  },
  // Finance chases overdue advances; the employee's own copy is sent by the emitter.
  'expense.advance.overdue': {
    mode: 'off',
    personIds: [],
    includeManagers: false,
    extraPersonIds: [],
    roleKeys: ['finance'],
  },
};

export const updateNotificationAudienceBodySchema = z
  .object({
    eventKey: z.enum(AUDIENCE_CONFIGURABLE_EVENT_KEYS),
    mode: notificationAudienceModeSchema,
    personIds: z.array(uuidSchema).max(100).default([]),
    /** Omitted = keep the stored value (or the default). */
    includeManagers: z.boolean().optional(),
    extraPersonIds: z.array(uuidSchema).max(50).optional(),
    roleKeys: z.array(z.string().trim().min(1).max(100)).max(20).optional(),
  })
  .refine((v) => v.mode !== 'hr_selected' || v.personIds.length > 0, {
    message: 'Choose at least one HR person, or use "All HR" or "No HR".',
    path: ['personIds'],
  });
export type UpdateNotificationAudienceBody = z.infer<typeof updateNotificationAudienceBodySchema>;

// ---------------------------------------------------------------------------
// Attendance reminder timings (attendance emails plan PR 2)
// ---------------------------------------------------------------------------

export const ATTENDANCE_REMINDER_MINUTES_MIN = 5;
export const ATTENDANCE_REMINDER_MINUTES_MAX = 120;
export const ATTENDANCE_REMINDER_MINUTES_DEFAULT = 15;
/** The morning pending-course digest: minutes after the shift starts. */
export const TRAINING_DIGEST_START_MINUTES_DEFAULT = 15;
/** The evening pending-course digest: minutes before the shift ends. */
export const TRAINING_DIGEST_END_MINUTES_DEFAULT = 60;

const reminderMinutesSchema = z
  .number()
  .int()
  .min(ATTENDANCE_REMINDER_MINUTES_MIN)
  .max(ATTENDANCE_REMINDER_MINUTES_MAX);

/**
 * When the employee's own attendance emails go out, company-wide (not per shift):
 *  - `shiftReminderMinutes`     BEFORE the shift starts ("your shift starts soon");
 *  - `clockOutReminderMinutes`  BEFORE the shift ends ("remember to clock out");
 *  - `checkInMissedMinutes`     AFTER the grace period ends with no check-in;
 *  - `clockOutMissedMinutes`    AFTER the shift ends while still clocked in.
 * Each 5–120 minutes; 15 when the company has never changed it.
 *
 * The same row also holds the two training digest moments (the "courses still pending" email):
 *  - `trainingDigestStartMinutes`  AFTER the shift starts (default 15);
 *  - `trainingDigestEndMinutes`    BEFORE the shift ends (default 60).
 */
export const attendanceReminderTimingsSchema = z.object({
  shiftReminderMinutes: reminderMinutesSchema,
  clockOutReminderMinutes: reminderMinutesSchema,
  checkInMissedMinutes: reminderMinutesSchema,
  clockOutMissedMinutes: reminderMinutesSchema,
  trainingDigestStartMinutes: reminderMinutesSchema,
  trainingDigestEndMinutes: reminderMinutesSchema,
});
export type AttendanceReminderTimings = z.infer<typeof attendanceReminderTimingsSchema>;

export const DEFAULT_ATTENDANCE_REMINDER_TIMINGS: AttendanceReminderTimings = {
  shiftReminderMinutes: ATTENDANCE_REMINDER_MINUTES_DEFAULT,
  clockOutReminderMinutes: ATTENDANCE_REMINDER_MINUTES_DEFAULT,
  checkInMissedMinutes: ATTENDANCE_REMINDER_MINUTES_DEFAULT,
  clockOutMissedMinutes: ATTENDANCE_REMINDER_MINUTES_DEFAULT,
  trainingDigestStartMinutes: TRAINING_DIGEST_START_MINUTES_DEFAULT,
  trainingDigestEndMinutes: TRAINING_DIGEST_END_MINUTES_DEFAULT,
};

export const attendanceReminderTimingsResponseSchema = attendanceReminderTimingsSchema.extend({
  /** False when nothing is stored and these are the defaults. */
  customised: z.boolean(),
  updatedAt: instantSchema.nullable(),
});
export type AttendanceReminderTimingsResponse = z.infer<
  typeof attendanceReminderTimingsResponseSchema
>;

/** A person offered in the recipient picker. */
export const recipientOptionPersonSchema = z.object({
  personId: uuidSchema,
  name: z.string(),
  email: z.string().nullable(),
});
export type RecipientOptionPerson = z.infer<typeof recipientOptionPersonSchema>;

export const listRecipientOptionsQuerySchema = z.object({
  /** Search people by name or email for "specific people". Omitted = no people search. */
  q: z.string().trim().min(1).max(100).optional(),
});

export const recipientOptionsSchema = z.object({
  /** Everyone currently holding an HR role (hr_admin / hr_manager) — for "Selected HR people". */
  hrPeople: z.array(recipientOptionPersonSchema),
  /** Every role in the company — for "Anyone with role". */
  roles: z.array(z.object({ key: z.string(), label: z.string() })),
  /** People matching `q` (max 20); empty without `q`. */
  people: z.array(recipientOptionPersonSchema),
  /** Names for ids already saved in any audience setting, so the picker can show them. */
  known: z.array(recipientOptionPersonSchema),
});
export type RecipientOptions = z.infer<typeof recipientOptionsSchema>;

// ---------------------------------------------------------------------------
// Router
// ---------------------------------------------------------------------------

export const notificationSettingsContract = c.router(
  {
    listChannels: {
      method: 'GET',
      path: '/channels',
      responses: {
        200: z.array(notificationChannelSettingSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'The full module x channel settings grid for the org',
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    updateChannels: {
      method: 'PUT',
      path: '/channels',
      body: updateNotificationChannelsBodySchema,
      responses: {
        200: z.array(notificationChannelSettingSchema),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Upsert one or more module x channel toggles',
      metadata: routeMeta('notification.config:manage'),
    },
    listEventSettings: {
      method: 'GET',
      path: '/events',
      responses: {
        200: z.array(notificationEventSettingSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary:
        'The explicit per-notification overrides for the org (no row = inherit from the module)',
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    updateEventSettings: {
      method: 'PUT',
      path: '/events',
      body: updateNotificationEventSettingsBodySchema,
      responses: {
        200: z.array(notificationEventSettingSchema),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Set or clear per-notification overrides; mandatory notices cannot be overridden',
      metadata: routeMeta('notification.config:manage'),
    },
    listAudience: {
      method: 'GET',
      path: '/audience',
      responses: {
        200: z.array(notificationAudienceSettingSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Who receives each configurable notification (one entry per configurable key)',
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    updateAudience: {
      method: 'PUT',
      path: '/audience',
      body: updateNotificationAudienceBodySchema,
      responses: {
        200: notificationAudienceSettingSchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Choose who receives one configurable notification (managers, HR, people, roles)',
      metadata: routeMeta('notification.config:manage'),
    },
    getAttendanceTimings: {
      method: 'GET',
      path: '/attendance-timings',
      responses: {
        200: attendanceReminderTimingsResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'When the employee attendance reminders go out (company-wide)',
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    updateAttendanceTimings: {
      method: 'PUT',
      path: '/attendance-timings',
      body: attendanceReminderTimingsSchema,
      responses: {
        200: attendanceReminderTimingsResponseSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Set when the employee attendance reminders go out (5–120 minutes each)',
      metadata: routeMeta('notification.config:manage'),
    },
    listRecipientOptions: {
      method: 'GET',
      path: '/audience/options',
      query: listRecipientOptionsQuerySchema,
      responses: {
        200: recipientOptionsSchema,
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'HR people, roles and a people search for the recipient picker',
      metadata: routeMeta(['notification.config:read', 'notification.config:manage']),
    },
    listDeliveryActivity: {
      method: 'GET',
      path: '/delivery-activity',
      query: listDeliveryActivityQuerySchema,
      responses: {
        200: paginatedSchema(notificationDeliveryActivityItemSchema),
        401: errorBodySchema,
        403: errorBodySchema,
      },
      summary: 'Paginated, filterable notification delivery log',
      metadata: routeMeta(['notification.delivery:read', 'notification.config:manage']),
    },
    getDeliverySummary: {
      method: 'GET',
      path: '/delivery-activity/summary',
      query: getDeliverySummaryQuerySchema,
      responses: {
        200: notificationDeliverySummarySchema,
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Sent / failed / suppressed counts for a period, per channel (delivery health)',
      metadata: routeMeta(['notification.delivery:read', 'notification.config:manage']),
    },
    exportDeliveryActivity: {
      method: 'GET',
      path: '/delivery-activity/export',
      query: exportDeliveryActivityQuerySchema,
      responses: {
        200: z.object({
          items: z.array(notificationDeliveryExportItemSchema),
          /** True when the period held more than NOTIFICATION_DELIVERY_EXPORT_MAX_ROWS rows. */
          truncated: z.boolean(),
        }),
        400: errorBodySchema,
        401: errorBodySchema,
        403: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Delivery log rows (with recipient name and email) for a bounded period, for CSV export',
      metadata: routeMeta('notification.delivery:read'),
    },
  },
  { pathPrefix: '/notification-settings' },
);
